export function relativeTime(ms) {
    if (!ms) return '';
    const diff = Date.now() - ms;
    const day = 86400000;
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < day) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
    if (diff < 365 * day) return `${Math.floor(diff / (30 * day))}mo`;
    return `${Math.floor(diff / (365 * day))}y`;
}

export function senderInitials(name) {
    if (!name) return '··';
    return name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();
}

// Chrome reports an empty (or octet-stream) MIME type for .aif/.aiff files,
// so a `type.startsWith('audio/')` check silently drops them. Fall back to a
// known-extension allowlist so AIFF (and other thinly-typed formats) upload.
const AUDIO_EXTENSIONS = new Set([
    'mp3', 'wav', 'wave', 'aif', 'aiff', 'aifc', 'm4a', 'm4b', 'aac',
    'flac', 'ogg', 'oga', 'opus', 'wma', 'alac', 'caf', 'mp2', 'mpga',
]);

export const AUDIO_ACCEPT = `audio/*,${[...AUDIO_EXTENSIONS].map((e) => `.${e}`).join(',')}`;

export function isAudioFile(file) {
    if (file?.type && file.type.startsWith('audio/')) return true;
    const name = file?.name || '';
    const dot = name.lastIndexOf('.');
    if (dot < 0) return false;
    return AUDIO_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

export function plural(n, word) {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// Human-readable byte size — fewer decimals as the number grows so sizes
// stay glanceable ("420 MB", "1.2 GB").
export function formatBytes(bytes) {
    if (!bytes || bytes < 0) return '0 MB';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let n = bytes;
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
    const decimals = i <= 1 || n >= 100 ? 0 : n >= 10 ? 1 : 2;
    return `${n.toFixed(decimals)} ${units[i]}`;
}

export function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// True when `child` is `parent` or nested inside it — used by drop zones to
// ignore dragleave events that fire while moving between inner elements.
export function isChildElement(parent, child) {
    let node = child;
    while (node) {
        if (node === parent) return true;
        node = node.parentNode;
    }
    return false;
}

export function cloneInstance(instance, patch) {
    const copy = Object.create(Object.getPrototypeOf(instance));
    Object.assign(copy, instance, patch || {});
    return copy;
}

// `redeemProjectLink` returns the project at the top level; `redeemShareLink`
// nests it under `payload` — accept both shapes here.
export function buildSharedProject(key, response) {
    const projectData = response?.project || response?.payload?.project || {};
    const albumArtUrl = response?.albumArtUrl
        || response?.payload?.albumArtUrl
        || null;
    const meta = projectData.metadata || {};
    const albumArtId = meta['album art'] || null;

    const project = {
        id: response.projectId,
        artistId: response.artistId,
        name: meta.name || 'Untitled',
        albumArtId,
        imageUrl: albumArtUrl,
        numberOfTracks: meta['track count'] || 0,
        duration: meta.duration || 0,
        artist: { id: response.artistId, name: '' },
        isShared: true,
        slug: key?.slug || null,
        token: key?.token || null,
        permission: response.permission || 'view',
    };

    const trackEntries = Object.entries(projectData.tracks || {});
    const tracks = trackEntries.map(([id, val]) => {
        const m = val?.metadata || {};
        const currRevId = m['current revision'];
        const revMeta = val?.revisions?.[currRevId]?.metadata || {};
        return {
            id,
            currRevId,
            name: m.name || 'Untitled',
            duration: revMeta.duration || 0,
            audioUrl: null,
            orderWeight: m['order weight'] || 0,
            isLocked: m.locked === true,
            project,
        };
    }).sort((a, b) => (a.orderWeight || 0) - (b.orderWeight || 0));

    return { project, tracks };
}

export function buildOptimisticSharedProject(preview, key) {
    return {
        id: preview.projectId,
        artistId: preview.artistId,
        name: preview.name || 'Loading…',
        imageUrl: preview.imageUrl || null,
        numberOfTracks: 0,
        duration: 0,
        artist: { id: preview.artistId, name: '' },
        isShared: true,
        slug: key?.slug || null,
        token: key?.token || null,
        permission: 'view',
    };
}
